import React from 'react';
import gorjancLogo from '../images/gorjanc-logo.svg';
import hvacSolutionsLogo from '../images/hvac-solutions-logo.svg';
import mugyverLogo from '../images/mugyver-logo.svg';
import albuquerquePlumbingLogo from '../images/albuquerque-plumbing-logo.svg';
import tampaBayPlumbingLogo from '../images/tampa-bay-plumbing.svg';
import daycoolLogo from '../images/daycool-logo.png';
import romanElectricLogo from '../images/roman-electric-logo.png';
import useMarketingProDetailData from '../hooks/useMarketingProDetailData';

export const reputationManagementData = () => {
  const {
    customerEngagementImage,
    heroVideoReputation,
    reputationManagementMp4,
    reviewSourcesMp4,
  } = useMarketingProDetailData();

  return {
    hero: {
      title: 'Increase visibility and trust with more 5-star reviews',
      description:
        'Take control of your online reputation with more reviews and consistent listings. Manage reviews from one place and gain deeper insights into your customers, technicians, and jobs.',
    },
    video: heroVideoReputation,
    copyImageSections: [
      {
        title: 'Drive customer engagement with your business',
        description:
          'Generate more quality reviews through automated review requests. Improve search rankings and build customer trust by making sure both your reviews and listings accurately reflect your business.',
        quote:
          'Marketing Pro - Reputation blows our other provider out of the water. We got 74 reviews in the first week and a half. It’s awesome!',
        author: 'Phil Gorjanc, General Manager',
        position: 'Gorjanc Comfort Services',
        logo: gorjancLogo,
        logoAlt: null,
        featuredImageUrl: customerEngagementImage.image,
        featuredImageAlt: customerEngagementImage.alt,
        fullWidth: false,
        imageRight: false,
      },
      {
        title: 'Show your customers you care',
        description:
          'Keep on top of all your reviews from one consolidated feed in ServiceTitan and get alerts so you can respond quickly to every positive and negative review.',
        quote:
          'You can respond to reviews in one place and it updates all your organic listings to the same exact info. It’s really amazing.',
        author: 'Stacy Fore',
        position: 'HVAC Solutions',
        logo: hvacSolutionsLogo,
        logoAlt: null,
        colClassName: 'marketing-pro-detail--customer-care-img',
        featuredVideo: reviewSourcesMp4?.image?.file?.url,
        imageRight: true,
      },
      {
        title: 'Match reviews to technicians, customers and jobs',
        description:
          'Get robust insights into which customers, jobs, and technicians are driving positive or negative reviews for your business.',
        quote:
          'Since setting up Marketing Pro- Reputation’s followup survey text….our reviews are going through the roof.',
        author: 'Brandon Day',
        position: 'Daycool Air',
        logo: daycoolLogo,
        logoAlt: 'Daycool logo',
        featuredVideo: reputationManagementMp4?.image?.file?.url,
        imageRight: false,
      },
    ],
    bottomVideo: {
      quote: (
        <>
          With Marketing Pro, we can select our audience,
          <br /> select our campaign and it’s automatically sent.
          <br /> We’ve seen a great return on that.
        </>
      ),
      author: 'April Diemer, CFO',
      logo: tampaBayPlumbingLogo,
      logoAlt: 'Tampa Bay Plumbing logo',
      videoSrc: 'https://servicetitan-1.wistia.com/medias/0yurul958v',
    },
  };
};

export const emailMarketingData = () => {
  const {
    campaignBuildingMp4,
    emailPerformanceImage,
    driveRevenueImage,
    heroVideoEmail,
  } = useMarketingProDetailData();

  return {
    hero: {
      title: 'Automatically follow up and generate opportunities with email',
      description:
        'Reach existing customers and generate more opportunities with email marketing campaigns in ServiceTitan Marketing Pro. Build hyper-targeted audiences, automate your email campaigns and track true ROI all from one place.',
    },
    video: heroVideoEmail,
    copyImageSections: [
      {
        title: 'Hyper-target your audience',
        description:
          'Create audiences based on customer information that you already have in ServiceTitan—like unsold estimates, aging equipment, and memberships that are expiring. This information automatically updates in real-time so you can truly “set and forget” your email campaigns!',
        quote: `To be able to send an email message and get that specific in your targeting - I haven't really been able to do in the past...not until Marketing Pro.`,
        author: 'Megan Bedford',
        position: 'Mugyver Consulting',
        logo: mugyverLogo,
        logoAlt: 'Mugyver logo',
        featuredVideo: campaignBuildingMp4?.image?.file?.url,
        colClassName: 'marketing-pro-overview--target-img',
        imageRight: false,
        contained: true,
      },
      {
        title: 'Drive significantly more revenue',
        description:
          'Hyper-targeted campaigns allow for email marketing that resonates with your customers, equating to higher open rates and more calls rolling in. The sky’s the limit.  The more targeted your audiences and campaigns are, the bigger ROI you can expect!',
        quote:
          'I had a target to reach at least $5,000 in the listed revenue at the end of the first month. We did $12,000.',
        author: 'Angela Pisanello',
        position: 'Roman Electric',
        logo: romanElectricLogo,
        logoAlt: 'Roman Electric logo',
        featuredImageUrl: driveRevenueImage.image,
        featuredImageAlt: driveRevenueImage.alt,
        colClassName: 'marketing-pro-detail--large-image',
        imageRight: true,
        contained: true,
      },
      {
        title: 'Know exactly how your emails are performing',
        description:
          'Go beyond just knowing which emails are being opened. With ServiceTitan Marketing Pro, you can see exactly which emails are driving revenue to your business. Track real ROI with both email performance and revenue attribution.',
        quote:
          'What Marketing Pro has allowed us to do is know what’s working and what’s not working and if something is not working I can cut it and keep our budget tight.',
        author: 'Vanessa Gonzalez',
        position: 'Albuquerque Plumbing, Heating & Cooling',
        logo: albuquerquePlumbingLogo,
        logoAlt: 'Albuquerque Plumbing logo',
        featuredImageUrl: emailPerformanceImage.image,
        featuredImageAlt: emailPerformanceImage.alt,
        colClassName: 'marketing-pro-detail--large-image',
        imageRight: false,
        contained: true,
      },
    ],
    bottomVideo: {
      quote: (
        <>
          With Marketing Pro, we can select our audience,
          <br /> select our campaign and it’s automatically sent.
          <br /> We’ve seen a great return on that.
        </>
      ),
      author: 'April Diemer, CFO',
      logo: tampaBayPlumbingLogo,
      logoAlt: 'Tampa Bay Plumbing logo',
      videoSrc: 'https://servicetitan-1.wistia.com/medias/0yurul958v',
    },
  };
};

export const directMailData = () => {
  const {
    campaignBuildingMp4,
    emailAutomationMp4,
    heroVideoDirectMail,
    measureRoiImage,
  } = useMarketingProDetailData();

  return {
    hero: {
      title: 'Direct mail campaigns without the hassle',
      description:
        'Automate your direct mail campaigns to your existing customers with ServiceTitan Marketing Pro. Send one postcard or thousands for one flat rate per piece.',
    },
    video: heroVideoDirectMail,
    copyImageSections: [
      {
        header: 'Build targeted postcard campaigns from setup to delivery in minutes—not weeks',
        title: 'Hyper-target your mailing lists',
        description:
          'Build audience lists based on your existing customer data, like unsold estimates or aging equipment. Send one postcard or thousands -- it will always be the right message to the right customers at the right time.',
        quote:
          'The average response rate to direct mail amounted to 4.9% for prospects and 9% for house lists.',
        author: 'ANA/DMA Response Rate Report 2018',
        logo: null,
        logoAlt: null,
        featuredVideo: campaignBuildingMp4?.image?.file?.url,
        imageRight: false,
        contained: true,
      },
      {
        title: 'Streamline the campaign building process',
        description:
          'Say goodbye to the hassle of designing, printing, mailing, and tracking direct mail campaigns. Create automated direct mail in minutes - just select your audience, upload your postcard and go.',
        quote:
          'Audiences are best reached through more frequent messages, automation and triggers based on specific needs.',
        author: 'PFL State of Multichannel Report 2020',
        logo: null,
        logoAlt: null,
        featuredVideo: emailAutomationMp4?.image?.file?.url,
        imageRight: true,
        contained: true,
      },
      {
        title: 'Measure your true ROI on every postcard',
        description:
          'Track deliverability of every postcard you send and see which ones are driving revenue. Get insights on maximizing revenue per contact so you can feel confident direct mail is working for you.',
        quote:
          '62% of consumers who responded to direct mail in the past three months made a purchase.',
        author: 'InfoTrends',
        logo: null,
        logoAlt: null,
        featuredImageUrl: measureRoiImage.image,
        featuredImageAlt: measureRoiImage.alt,
        imageRight: false,
      },
    ],
    bottomVideo: {
      quote: (
        <>
          With Marketing Pro, we can select our audience,
          <br /> select our campaign and it’s automatically sent.
          <br /> We’ve seen a great return on that.
        </>
      ),
      author: 'April Diemer, CFO',
      logo: tampaBayPlumbingLogo,
      logoAlt: 'Tampa Bay Plumbing logo',
      videoSrc: 'https://servicetitan-1.wistia.com/medias/0yurul958v',
    },
  };
};
